import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import { States } from "../../shared/config/constant.module";
import template from './tankMetricsReport.tpl.html';
import { Plant } from "../../models/navigation";
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import { StateParams } from "../../common";
import LoaderServiceName, { LoaderService } from "../../components/loader/loader.service";
import ReportServiceName, { ReportService } from "../../services/reports/report.service";
import DateUtilityService from "../../services/utility/dateutility.service";
import NavigationServiceName, { NavigationService } from "../../services/navigation/navigation.service";
import { ICommonQueryingReport } from './commonQueryingReport';

let requires = [LoaderServiceName, ReportServiceName, NavigationServiceName];

interface TankMetricsReportController extends ICommonQueryingReport {
  results: apsViewModels.TankMetricsReportItem[];
  searchPerformed: boolean;
  hideShopSelection: boolean;

  filteredResults(): apsViewModels.TankMetricsReportItem[];
  generateReport(): void;
  formatTime(time: Date): string;
  exportToCsv(): any[];
}

export interface TankMetricsReportParams extends StateParams {
  reportPlantId: string;
  startDate: string;
  endDate: string;
  autoRun: boolean;
}

class TankMetricsReportCtrl implements TankMetricsReportController {
  plants: Plant[];
  selectedPlant: Plant;
  startDate: Date;
  endDate: Date;
  form: angular.IFormController;
  selectedShop: apsViewModels.ShopNavigation;
  requireShopSelection: boolean;
  uiStartDate: Date;
  uiEndDate: Date;
  requirePlantSelection: boolean;
  hideShopSelection: boolean;

  private $stateParams: TankMetricsReportParams;
  private $state: angular.ui.IStateService;

  results: apsViewModels.TankMetricsReportItem[];
  searchPerformed: boolean;

  loaderService: LoaderService;
  reportService: ReportService;
  private $q: angular.IQService;

  constructor(
    $stateParams: TankMetricsReportParams,
    $state: angular.ui.IStateService,
    $q: angular.IQService,
    navigationService: NavigationService,
    reportService: ReportService,
    loaderService: LoaderService
  ) {
    this.$q = $q;
    this.$state = $state;
    this.$stateParams = $stateParams;
    this.loaderService = loaderService;
    this.reportService = reportService;

    this.requirePlantSelection = true;
    this.hideShopSelection = true;

    var today = moment().startOf('day');
    this.startDate = today.clone().subtract(1, 'month').toDate();
    this.endDate = today.toDate();

    var promise = navigationService.getNavigation().then((navModel) => {
      this.plants = navModel.plants();
      this.selectedPlant = navModel.selectedPlant;
      this.updatePlant();

      return this.loadState($stateParams);
    });

    loaderService.loadingPromise(promise);
  }

  filteredResults(): apsViewModels.TankMetricsReportItem[] {
    return this.results;
  }

  updatePlant(): void { }

  loadState(params: TankMetricsReportParams): angular.IPromise<any> {
    if (params.reportPlantId && params.reportPlantId !== params.plantId) {
      var plant = _.find(this.plants, { id: params.reportPlantId });
      if (plant) {
        this.selectedPlant = plant;
      }
    }

    if (params.startDate) {
      var startDate = moment(params.startDate);
      if (startDate.isValid()) {
        this.startDate = startDate.toDate();
      }
    }

    if (params.endDate) {
      var endDate = moment(params.endDate);
      if (endDate.isValid()) {
        this.endDate = endDate.toDate();
      }
    }

    if (params.autoRun) {
      return this.runReport();
    }

    return this.$q.when();
  }

  saveState() {
    var reportPlantId = this.selectedPlant ? this.selectedPlant.id : undefined;
    if (reportPlantId === this.$stateParams.plantId) {
      reportPlantId = undefined;
    }
    var params: TankMetricsReportParams = {
      plantId: this.$stateParams.plantId,
      shopId: this.$stateParams.shopId,
      userId: this.$stateParams.userId,
      reportPlantId: reportPlantId,
      startDate: this.startDate ? this.startDate.toISOString() : undefined,
      endDate: this.endDate ? this.endDate.toISOString() : undefined,
      autoRun: true
    };

    this.$state.transitionTo(States.TANK_METRICS_REPORT, params);
  }

  generateReport() {
    if (this.form.$invalid) {
      return;
    }

    this.saveState();

    this.loaderService.loadingPromise(this.runReport());
  }

  private runReport(): angular.IPromise<any> {
    var startDateStart = moment(this.startDate).startOf('day').toDate();
    var tzOffsetStartDate = DateUtilityService.localToTimeZone(startDateStart, this.selectedPlant.timeZoneId).toDate();

    var endDateEnd = moment(this.endDate || new Date()).endOf('day').toDate();
    var tzOffsetEndDate = DateUtilityService.localToTimeZone(endDateEnd, this.selectedPlant.timeZoneId).toDate();

    return this.reportService.getTankMetricsReport(this.selectedPlant.id, tzOffsetStartDate, tzOffsetEndDate)
      .then(reportItems => {
        this.results = reportItems;
        this.searchPerformed = true;
      });
  }

  exportToCsv() {
    function repeat(value, total) {
      var arr = [value];
      for (var i = 1; i < total; i++) {
        arr.push('');
      }
      return arr;
    }

    var headerRow1 =
      ['', '', '']
        .concat(repeat('KPI', 13))
        .concat(repeat('KPO', 12));

    const headerRow2 = [
      '', '', '',
      'Pull', 'Cullet', 'Gas', 'Air/Fuel',
      'Boost A', 'Boost B', 'Boost C', 'Total Boost',
      'Gas', 'Electric', 'Melt Energy', 'Melt Energy Kcal kg', 'Glass Density',
      'BW OPT', 'Bottom 3', 'Throat', 'Seeds',
      'NOx', 'SOx', 'Cord',
      'Transmission (Amber)', 'Color (Flint)',
      'Pressure', 'Batch Line', 'Batch Moisture'
    ];

    const headerRow3 = [
      '', 'Tank', 'Date',
      'TPD', '%', 'MCFH', 'Ratio',
      'KW', 'KW', 'KW', 'KW',
      'MMBTU/ton', 'MMBTU/ton', 'MMBTU/ton',
      '',
      'Gm/CC',
      'F', 'F', 'F',
      'in3', '#/ton', '#/ton',
      '', '', '', '', '', ''
    ];

    var dataRows = _.map<apsViewModels.TankMetricsReportItem, any>(this.filteredResults(), (x) => {
      return {
        plant: this.selectedPlant.name,
        tank: x.tank,
        date: this.formatTime(x.scheduledOn),
        pull: x.pull,
        cullet: x.cullet,
        gas: x.gas,
        air: x.air,
        boostA: x.boostA,
        boostB: x.boostB,
        boostC: x.boostC,
        totalBoost: x.totalBoost,
        gasEnergy: x.gasEnergy,
        electricEnergy: x.electricEnergy,
        meltEnergy: x.meltEnergy,
        meltEnergyKcalKg: x.meltEnergyKcalKg,
        glassDensity: x.glassDensity,
        BWOPT: x.BWOPT,
        bottom3: x.bottom3,
        throat: x.throat,
        seeds: x.seeds,
        NOx: x.NOx,
        SOx: x.SOx,
        cord: x.cord,
        transmissionAmber: x.transmissionAmber,
        colorFlint: x.colorFlint,
        pressure: x.pressure,
        batchLine: x.batchLine,
        batchMoisture: x.batchMoisture
      };
    })

    return [headerRow1, headerRow2, headerRow3].concat(dataRows);

  }

  formatTime(time: Date): string {
    return DateUtilityService.formatDate(time, this.selectedPlant.timeZoneId);
  }
}

export default angular.module('aps.controller.tankMetricsReport', requires)
  .config(function ($stateProvider: angular.ui.IStateProvider) {
    $stateProvider.state({
      name: States.TANK_METRICS_REPORT,
      url: '/admin/reports/tankMetrics?startDate&endDate&autoRun&reportPlantId',
      params: {
        startDate: { dynamic: true },
        endDate: { dynamic: true },
        autoRun: { dynamic: true },
        reportPlantId: { dynamic: true },
        reportShopId: { dynamic: true }
      },
      controller: 'TankMetricsReportCtrl as vm',
      template: template,
      data: {
        pageTitle: 'Tank Metrics Report'
      }
    });
  })
  .controller('TankMetricsReportCtrl', TankMetricsReportCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/reports/tankMetricsReport.controller.ts